import axios from 'axios';
import { isEmpty, isEqual, set } from 'lodash';
import Notifier from 'common/helpers/Notifier';
import {
  SET_SEARCH_FETCHING,
  UNSET_SEARCH_FETCHING,
  FETCH_SEARCH_INDEX_RESULTS,
  SET_SEARCH_INDEX_FETCHING,
  UNSET_SEARCH_INDEX_FETCHING,
} from 'actions';
import {
  CLIENT_RESULTS_COUNT,
  DEFAULT_RECORD_TYPE,
  DEFAULT_SEARCH_SCOPE,
  MEDICAID_ID_SCOPE,
} from 'src/components/Search/constants';
import { coreApi } from 'src/api/config';
import {
  cl1292clientsIndexMaterializedView,
  cl1540simplePaging,
  cl2234searchIncludesNetworkResults,
} from 'common/utils/FeatureFlags/flags';

const resources = {
  contact: 'person',
  cases_servicecase: 'case',
};

function buildSearchParams({
  model,
  query,
  scope,
  groupId,
  networkId,
  isCC,
  includeNetworkSearchResults,
}) {
  const params = {};

  switch (model) {
    case 'contact':
      params.query = query;

      if (includeNetworkSearchResults && isCC) {
        params['client_relationships.provider.networks'] = networkId;
      } else {
        params['client_relationships.provider'] = groupId;
      }

      if (scope === MEDICAID_ID_SCOPE) {
        params.include_external_id = true;
        params.scope = scope;
      }
      break;
    case 'cases_servicecase':
      params.query = query;
      params.provider = groupId;
      break;
    default:
      break;
  }

  return params;
}

function buildPageOptions({
  model, page, per, clientsIndexMaterializedView, simplePaging, cancelMethod,
}) {
  const pageOptions = {
    sort: 'similarity',
    page: { number: page, size: per },
    httpConfig: {
      cancelToken: new axios.CancelToken((c) => { cancelMethod(c); }),
    },
  };

  if (model === 'contact') {
    pageOptions.include = 'addresses';

    if (clientsIndexMaterializedView) {
      pageOptions.customParams = {
        fields: {
          person: 'first_name,last_name,phone_numbers,email_addresses,date_of_birth,external_id,addresses',
        },
      };
    } else if (simplePaging) {
      pageOptions.customParams = { simple_paging: true };
    }
  }

  return pageOptions;
}

async function populateAddresses(payload, model, clientsIndexMaterializedView) {
  if (isEqual(model, 'contact') && !clientsIndexMaterializedView) {
    await Promise.all(payload.data.data.map(async (person) => {
      if (!isEmpty(person.addresses)) {
        const addresses = await coreApi.query('addresses', { person: person.id });
        set(person, 'addresses', addresses.data.data);
      }
    }));
  }
}

async function fetchAndPopulateData({
  resource, params, pageOptions, model, clientsIndexMaterializedView,
}) {
  const payload = await coreApi.query(resource, params, pageOptions);

  await Promise.all([
    coreApi.populateRelationship('person', 'person', payload.data.data),
    coreApi.populateRelationship('program', 'program', payload.data.data),
    coreApi.populateRelationship('service', 'service', payload.data.data),
    coreApi.populateRelationship('primary_worker', 'employee', payload.data.data),
  ]);

  await populateAddresses(payload, model, clientsIndexMaterializedView);

  // Transform to legacy structure
  payload.data.data = {
    [model]: {
      ...payload.data,
      data: payload.data.data.map((d) => ({ item: { result: d } })),
    },
  };

  return payload;
}

export default function fetchSearchResults({
  groupId,
  query,
  scope = DEFAULT_SEARCH_SCOPE,
  model = DEFAULT_RECORD_TYPE,
  page = 1,
  per = CLIENT_RESULTS_COUNT,
  isIndexFetch = false,
  networkId,
  isCC = false,
}) {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const clientsIndexMaterializedView = cl1292clientsIndexMaterializedView(state);
      const includeNetworkSearchResults = cl2234searchIncludesNetworkResults(state);
      const simplePaging = cl1540simplePaging(state);
      let cancelMethod;
      let resource = resources[model];

      if (model === 'contact' && clientsIndexMaterializedView) {
        resource = 'people_view';
      }

      const params = buildSearchParams({
        model,
        query,
        scope,
        groupId,
        networkId,
        isCC,
        includeNetworkSearchResults,
      });

      const pageOptions = buildPageOptions({
        model,
        page,
        per,
        clientsIndexMaterializedView,
        simplePaging,
        cancelMethod: (c) => { cancelMethod = c; },
      });

      if (isIndexFetch) {
        dispatch({ type: SET_SEARCH_INDEX_FETCHING, cancel: cancelMethod });
      } else {
        dispatch({ type: SET_SEARCH_FETCHING, cancel: cancelMethod });
      }

      const payload = await fetchAndPopulateData({
        resource, params, pageOptions, model, clientsIndexMaterializedView,
      });

      if (isIndexFetch) {
        dispatch({ type: FETCH_SEARCH_INDEX_RESULTS, payload });
        dispatch({ type: UNSET_SEARCH_INDEX_FETCHING });
      }

      dispatch({ type: UNSET_SEARCH_FETCHING });
      return payload.data.data;
    } catch (error) {
      if (!isEmpty(error.message)) {
        dispatch({ type: UNSET_SEARCH_FETCHING });
        dispatch({ type: UNSET_SEARCH_INDEX_FETCHING });
      }
      if (error.message !== 'canceled') {
        Notifier.handleErrors(error);
      }
      return error;
    }
  };
}
